import { BaseListViewStore, DateTimeCellData, HyperlinkCellData } from 'core/stores';
import { TableFilterTemplate, TableFilterDateTemplate, TableFilterNumericTemplate } from 'themes/components';
import { isSuperAdminOrganization, isWarehouse, notificationError, TableFilterUtility } from 'core/utils';
import { observable, action, makeObservable } from 'mobx';
import { BaasicTableSettingsViewStore } from 'common/stores';
import { ReceivingStore } from 'modules/receiving/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, receivingStatus } from 'common/constants';
import { localStorageHandler } from 'core/services';
import { checkForColumnFilter, checkForColumnWidth, RemoveFiltersModalUtility } from 'core/utils';
import { ApplicationService } from 'common/data';
import _ from 'lodash';

class ReceivingManualListViewStore extends BaseListViewStore {
	@observable change = false;
	selectedItem = null;
	localStorageWidthKey = 'receivings-manual-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ReceivingStore(rootStore),
			queryParams: {
				embed: [
					'receivingStatus',
					'receivingType',
					'warehouseTo',
					'warehouseFrom',
					'receivingProducts',
					'receivingProducts.product',
					'seller',
				],
				filter: localStorageHandler.get('receiving-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'ref',
						title: 'RECEIVINGVIEW.REF',
						dataCell: HyperlinkCellData,
						onClick: dataItem => this.routes.detail(dataItem.id),
						width: 200,
						selected: true,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
						filterApiParams: {
							receivingType: 1,
						},
					},
					{
						key: 'receivingStatus.name',
						title: 'RECEIVINGVIEW.STATUS',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						selected: false,
						sortable: true,
						lookupConstant: lookupRouteName.receivingStatus,
						filterApiParams: {
							ignoreSystemValues: false,
						},
					},
					{
						key: 'seller.applicationTitle',
						title: 'RECEIVINGVIEW.SELLER',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						useAlias: true,
						selected: false,
						sortable: true,
						sellerOnly: true,
						dontSplit: true,
						enabled: isWarehouse(rootStore) || isSuperAdminOrganization(rootStore),
						service: 'applicationService',
					},
					{
						key: 'receivingType.name',
						title: 'RECEIVINGVIEW.TYPE',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						selected: false,
						sortable: true,
						type: 'lookup',
						lookupConstant: lookupRouteName.receivingType,
					},
					{
						key: 'estimatedReceivingDate',
						title: 'RECEIVINGVIEW.ESTIMATEDRECEIVINGDATE',
						dataCell: DateTimeCellData,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'dateCreated',
						title: 'RECEIVINGVIEW.CREATEDDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'LLL',
						useLocalTime: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'actualReceivingDate',
						title: 'RECEIVINGVIEW.ACTUALRECEIVINGDATE',
						dataCell: DateTimeCellData,
						useLocalTime: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'warehouseTo.applicationTitle',
						title: 'RECEIVINGVIEW.WAREHOUSETO',
						service: 'applicationService',
						useAlias: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						filterApiParams: {
							warehouseOnly: true,
						},
					},
					{
						key: 'warehouseFrom.applicationTitle',
						title: 'RECEIVINGVIEW.WAREHOUSEFROM',
						service: 'applicationService',
						useAlias: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						filterApiParams: {
							warehouseOnly: true,
						},
					},
					{
						key: 'cost',
						title: 'RECEIVINGVIEW.COST',
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
				],
				sortObject: [
					{
						field: 'ref',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: item => this.routes.edit(item.id),
					onDelete: item =>
						[receivingStatus.arrivedAtPort, receivingStatus.inTransit].includes(item.receivingStatus.abrv) &&
						item.receivingProducts.some(x => x.isReceived === true)
							? this.rootStore.notificationStore.error('LOOKUPDELETEERROR.RECEIVINGPORT')
							: this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
				},
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission('receivingSection.update'),
				onDelete: () => this.authStore.hasPermission('receivingSection.remove'),
			},
			routes: {
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.receiving.edit', {
						id,
					}),
				detail: id =>
					this.rootStore.routerStore.goTo('master.app.main.receiving.detail', {
						id,
					}),
				create: () => this.rootStore.routerStore.goTo('master.app.main.receiving.manual.list.add', {}),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (isSuperAdminOrganization(this.rootStore) || isWarehouse(this.rootStore)) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		await this.queryUtility.initialize(this.findContainers);
		checkForColumnFilter(localStorageHandler.get('receiving-manual-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'receiving-manual-table-data';
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(this.rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [this.listStore.service, new ApplicationService(this.rootStore.app.baasic.apiClient)],
		});
		this.setButtons();
		this.setHeaderModes();
		this.setSearchVisible();
		this.resume();
	};

	@action.bound setAsChanged() {
		this.change = !this.change;
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'receivingSection.create',
					text: 'RECEIVINGVIEW.CREATE',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
			],
		});
	}

	@action.bound
	deleteReceiving = async item => {
		this.suspend();
		try {
			await this.listStore.deleteReceiving(item);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.RECEIVING', { name: item.ref }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			if (err.data != null) {
				this.rootStore.notificationStore.error([err.data, { name: item.ref }]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	@action.bound
	expandChange = evt => {
		evt.dataItem.expanded = evt.value;
		this.setAsChanged();
	};

	@action.bound
	findContainers = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ReceivingTableSettings',
			this.setAsChanged,
			this.findContainers
		);
		this.queryUtility.setFilterParams(this, { receivingType: 1 }, this.tableFilter);
		await this.listStore.findReceiving(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		_.forEach(this.listStore.response.item, item => {
			if (item.warehouseFromId === null && item.warehouseFromName) {
				item.warehouseFrom = {
					applicationTitle: item.warehouseFromName,
				};
			}
			if (item.warehouseToId === null && item.warehouseToName) {
				item.warehouseTo = {
					applicationTitle: item.warehouseToName,
				};
			}
		});
		this.queryUtility.setFilterPersistance(this, 'receiving-manual-filter', 'receiving-manual-table-data');
		this.setButtons();
		this.resume();
	};

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deleteReceiving(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['LOOKUPDELETECONFIRM.RECEIVING', { name: item.ref }],
			data: item,
		};
	};
}

export default ReceivingManualListViewStore;



// WEBPACK FOOTER //
// ./src/modules/receiving/stores/manual/ReceivingManualListViewStore.js